import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ProcessingJob } from '../services/api';

export interface FileWithDocId {
  file: File;
  documentId: number | string | null;
}

export interface SavedActivity {
  id: string;
  name: string;
  uploadedFiles: FileWithDocId[] | File[]; // Support both old and new formats
  selectedFiles: Set<number>;
  actionHistory: Array<{ action: string; result: ProcessingJob }>;
  savedAt: Date;
  activityType?: 'contract-review' | 'legal-questions' | 'will-generation' | 'bill-of-sale' | 'legal-letters' | 'ai-advisor'; // Optional for backward compatibility
  // Will generation specific data
  willData?: {
    country: string;
    state: string;
    municipality: string;
    willFields: { [key: string]: any }; // Allow flexibility for field formats
    fieldGroups: string[];
    groupInstances: any; // Allow flexibility for different instance formats
    generatedWill: string;
    willTemplate: string;
  };
  // Bill of sale specific data
  billOfSaleData?: {
    country: string;
    state: string;
    municipality: string;
    bosFields: { [key: string]: any }; // Allow flexibility for field formats
    fieldGroups: string[];
    groupInstances: any; // Allow flexibility for different instance formats
    generatedBillOfSale: string;
    bosTemplate: string;
  };
}

interface ActivitiesState {
  activities: SavedActivity[];
  currentActivityId: string | null;

  addActivity: (activity: SavedActivity) => void;
  updateActivity: (activity: SavedActivity) => void;
  removeActivity: (id: string) => void;
  deleteActivity: (id: string) => void; // Alias for removeActivity
  clearAllActivities: () => void; // Clear all activities
  setCurrentActivityId: (id: string | null) => void;
  getActivityById: (id: string) => SavedActivity | undefined;
}

export const useActivitiesStore = create<ActivitiesState>()(
  persist(
    (set, get) => ({
      activities: [],
      currentActivityId: null,

      addActivity: (activity) => {
        set((state) => ({
          activities: [activity, ...state.activities],
        }));
      },

      updateActivity: (activity) => {
        set((state) => ({
          activities: [
            activity,
            ...state.activities.filter((a) => a.id !== activity.id),
          ],
        }));
      },

      removeActivity: (id) => {
        set((state) => ({
          activities: state.activities.filter((a) => a.id !== id),
        }));
      },

      deleteActivity: (id) => {
        // Alias for removeActivity
        set((state) => ({
          activities: state.activities.filter((a) => a.id !== id),
        }));
      },

      clearAllActivities: () => {
        set({
          activities: [],
          currentActivityId: null,
        });
      },

      setCurrentActivityId: (id) => {
        set({ currentActivityId: id });
      },

      getActivityById: (id) => {
        return get().activities.find((a) => a.id === id);
      },
    }),
    {
      name: 'activities-storage',
      // Custom serialization to handle Set and Date objects
      partialize: (state) => ({
        activities: state.activities.map((a) => ({
          ...a,
          selectedFiles: Array.from(a.selectedFiles),
          savedAt: a.savedAt.toISOString(),
        })),
        currentActivityId: state.currentActivityId,
      }),
      // Custom deserialization
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.activities = state.activities.map((a: any) => ({
            ...a,
            selectedFiles: new Set(a.selectedFiles),
            savedAt: new Date(a.savedAt),
          }));
        }
      },
    }
  )
);
